import React, { useCallback, useEffect, useMemo, useState } from 'react';

import currencyFormatter from 'currency-formatter';
import { Link as NavLink } from '~/lib/react-router-dom';

import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, Typography } from '@mui/material';
import { DataGridPro, GridColDef, GridRowParams } from '@mui/x-data-grid-pro';

import { useGetEligibleCreditReversals } from '~/hooks/apis/orders/useGetEligibleCreditReversals';

import type { CreditReversalOrder } from '~/services/OrdersService';
import { formatDateWeekDayShortMonth } from '~/services/TimeService';

import GridPagination from '../../Common/Elements/GridPagination';

import CashPaymentsDetailPanel from './CashPaymentsDetailPanel';
import { ROW_CLASS_INELIGIBLE } from './constants';
import type { EligibleCreditReversalsModalProps } from './types';

function formatMaxReversal(row: CreditReversalOrder, currency: string): string {
  if (row.maxReversal <= 0) {
    return '—';
  }
  const code = row.currency || currency;
  return `${currencyFormatter.format(row.maxReversal, { code, precision: 2 })} ${code}`;
}

const CREDIT_REVERSAL_COLUMNS = (currency: string): Array<GridColDef<CreditReversalOrder>> => [
  {
    field: 'transactionDate',
    headerName: 'Transaction Date',
    sortable: false,
    width: 160,
    valueFormatter: (value: string) => (value ? formatDateWeekDayShortMonth(value) : '—'),
  },
  {
    field: 'orderId',
    headerName: 'Order ID',
    sortable: false,
    width: 280,
    renderCell: (params) => (
      <Link component={NavLink} to={`/purchases/${params.value}`} underline="hover">
        {params.value}
      </Link>
    ),
  },
  {
    field: 'maxReversal',
    headerName: 'Maximum Reversal Amount',
    sortable: false,
    width: 200,
    renderCell: (params) => formatMaxReversal(params.row, currency),
  },
  {
    field: 'packageDetails',
    headerName: 'Package Details',
    sortable: false,
    flex: 1,
    minWidth: 140,
    valueFormatter: (value: string | null) => value ?? '—',
  },
  {
    field: 'bookingId',
    headerName: 'Booking ID',
    sortable: false,
    width: 140,
    valueFormatter: (value: string | null) => value ?? '—',
  },
  {
    field: 'checkIn',
    headerName: 'Check-In',
    sortable: false,
    width: 120,
    valueFormatter: (value: string | null) => value ?? '—',
  },
  {
    field: 'checkOut',
    headerName: 'Check-Out',
    sortable: false,
    width: 120,
    valueFormatter: (value: string | null) => value ?? '—',
  },
];

export default function EligibleCreditReversalsModal({
  currency,
  onClose,
  open,
  userId,
}: EligibleCreditReversalsModalProps) {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  const page = paginationModel.page + 1;
  const limit = paginationModel.pageSize;

  const { data, isLoading } = useGetEligibleCreditReversals(userId, page, limit, open);

  const rows = data?.result ?? [];
  const total = data?.total ?? 0;

  const columns = useMemo(() => CREDIT_REVERSAL_COLUMNS(currency), [currency]);

  const handleRowClick = useCallback((params: GridRowParams<CreditReversalOrder>) => {
    if (!params.row.isEligibleForCreditReversal) {
      return;
    }
    setSelectedOrderId((prev) => (prev === params.row.orderId ? null : params.row.orderId));
  }, []);

  useEffect(() => {
    setSelectedOrderId(null);
  }, [page]);

  const getRowClassName = useCallback((params: { row: CreditReversalOrder }) => {
    return !params.row.isEligibleForCreditReversal ? ROW_CLASS_INELIGIBLE : '';
  }, []);

  const getDetailPanelContent = useCallback(
    ({ row }: { row: CreditReversalOrder }) => <CashPaymentsDetailPanel row={row} />,
    [],
  );

  const getDetailPanelHeight = useCallback(() => 'auto' as const, []);

  const detailPanelExpandedRowIds = useMemo(() => (selectedOrderId ? [selectedOrderId] : []), [selectedOrderId]);

  const handleDetailPanelExpandedRowIdsChange = useCallback((rowIds: Array<string>) => {
    setSelectedOrderId(rowIds.length > 0 ? rowIds[0] : null);
  }, []);

  return (
    <Dialog fullWidth maxWidth="xl" onClose={onClose} open={open}>
      <DialogTitle>Credit Reversal</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
          Select an order to process credit reversal.
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Maximum Reversal Amount</strong> is the lesser of the total cash paid and the total amount refunded as
          travel credits for this order, minus any credit reversals already processed. Rows that are not eligible are
          greyed out.
        </Alert>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            '& .credit-reversal-row-ineligible': {
              opacity: 0.5,
              backgroundColor: 'action.hover',
              pointerEvents: 'none',
            },
          }}
        >
          <DataGridPro
            rows={rows}
            rowCount={total}
            columns={columns}
            getRowId={(row) => row.orderId}
            getRowClassName={getRowClassName}
            onRowClick={handleRowClick}
            rowSelectionModel={selectedOrderId ? [selectedOrderId] : []}
            getDetailPanelContent={getDetailPanelContent}
            getDetailPanelHeight={getDetailPanelHeight}
            detailPanelExpandedRowIds={detailPanelExpandedRowIds}
            onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
            loading={isLoading}
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            slots={{ pagination: GridPagination }}
            slotProps={{
              pagination: {
                count: total,
                page: paginationModel.page,
                rowsPerPage: paginationModel.pageSize,
              },
            }}
            disableColumnFilter
            disableColumnMenu
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>
        <Button color="primary" disabled variant="contained">
          Next
        </Button>
      </DialogActions>
    </Dialog>
  );
}
